import { useId, useState } from 'react';
import { ChevronsUpDown, Search } from 'lucide-react';
import { useUiText, type UiTextKey } from '../lib/ui-text';
import { Badge } from './badge';
import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';

export type RoleCategory = 'clinical' | 'business' | 'administration' | 'custom';

export interface PickerRole {
  id: string;
  name: string;
  displayName: string;
  description?: string | null;
  /** Sent by newer Rudi releases; older ones are grouped by role name. */
  category?: RoleCategory;
  isBuiltin?: boolean;
  isActive?: boolean;
}

const CATEGORIES: RoleCategory[] = ['clinical', 'business', 'administration', 'custom'];
const CLINICAL = new Set([
  'principal_investigator',
  'study_doctor',
  'study_nurse',
  'clinical_trials_manager',
]);
const BUSINESS = new Set([
  'feasibility_manager',
  'clinical_operations_manager',
  'customer_support',
]);
const ADMINISTRATION = new Set([
  'tenant_admin',
  'team_admin',
  'privacy_admin',
  'compliance_admin',
  'email_content_reviewer',
]);

export function roleCategory(role: PickerRole): RoleCategory {
  if (role.category && CATEGORIES.includes(role.category)) return role.category;
  if (CLINICAL.has(role.name)) return 'clinical';
  if (BUSINESS.has(role.name)) return 'business';
  if (ADMINISTRATION.has(role.name)) return 'administration';
  return role.isBuiltin ? 'administration' : 'custom';
}

type Tab = 'all' | RoleCategory | 'selected';
const TAB_LABELS: Record<Tab, UiTextKey> = {
  all: 'roleCategoryAll',
  clinical: 'roleCategoryClinical',
  business: 'roleCategoryBusiness',
  administration: 'roleCategoryAdministration',
  custom: 'roleCategoryCustom',
  selected: 'roleCategorySelected',
};

function sameRoles(a: string[], b: string[]) {
  return a.length === b.length && a.every(id => b.includes(id));
}

/**
 * Choose several roles from a popover with search and category tabs.
 * With `applyOnClose` the choice is kept as a draft and reported once the
 * popover closes, so a saved-on-change member row sends one update.
 */
export function RolePicker({
  label,
  roles,
  value,
  onChange,
  disabled = false,
  applyOnClose = false,
}: {
  label: string;
  roles: PickerRole[];
  value: string[];
  onChange: (roleIds: string[]) => void;
  disabled?: boolean;
  applyOnClose?: boolean;
}) {
  const uiText = useUiText();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [draft, setDraft] = useState(value);
  const selected = applyOnClose && open ? draft : value;
  const update = (next: string[]) => (applyOnClose ? setDraft(next) : onChange(next));
  const changeOpen = (next: boolean) => {
    if (next) {
      setDraft(value);
      setQuery('');
      setTab('all');
    } else if (applyOnClose && !sameRoles(draft, value)) {
      onChange(draft);
    }
    setOpen(next);
  };

  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const matches = roles.filter(role => {
    const text = `${role.displayName} ${role.name} ${role.description ?? ''}`.toLocaleLowerCase();
    return terms.every(term => text.includes(term));
  });
  const inTab = (current: Tab) =>
    matches.filter(role =>
      current === 'all'
        ? true
        : current === 'selected'
          ? selected.includes(role.id)
          : roleCategory(role) === current
    );
  const tabs: Tab[] = [
    'all',
    ...CATEGORIES.filter(category => roles.some(role => roleCategory(role) === category)),
    ...(selected.length > 0 ? (['selected'] as const) : []),
  ];
  const activeTab = tabs.includes(tab) ? tab : 'all';
  const chosen = roles.filter(role => selected.includes(role.id));
  const summary = chosen.map(role => role.displayName).join(', ') || uiText('chooseRoles');

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={`${label}: ${summary}`}
          className="h-auto min-h-10 w-full max-w-xl justify-between gap-2 py-2 text-left font-normal"
        >
          <span className="flex min-w-0 flex-wrap gap-1">
            {chosen.length === 0 && (
              <span className="text-muted-foreground">{uiText('chooseRoles')}</span>
            )}
            {chosen.slice(0, 3).map(role => (
              <Badge key={role.id} variant="secondary" className="rounded-3xl font-normal">
                {role.displayName}
              </Badge>
            ))}
            {chosen.length > 3 && (
              <Badge variant="outline" className="rounded-3xl font-normal">
                {uiText('moreRoles', { count: chosen.length - 3 })}
              </Badge>
            )}
          </span>
          <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={label}
        className="w-[min(28rem,calc(100vw-2rem))] space-y-3 p-3"
      >
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label={uiText('searchRoles')}
            placeholder={uiText('searchRoles')}
            className="pl-9"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </div>
        <Tabs value={activeTab} onValueChange={value => setTab(value as Tab)}>
          <TabsList className="h-auto w-full flex-wrap justify-start">
            {tabs.map(current => (
              <TabsTrigger key={current} value={current} className="gap-1">
                {uiText(TAB_LABELS[current])}
                <span className="text-xs text-muted-foreground">{inTab(current).length}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(current => (
            <TabsContent key={current} value={current}>
              <div
                role="group"
                aria-label={`${label} · ${uiText(TAB_LABELS[current])}`}
                className="max-h-72 space-y-1 overflow-y-auto pr-1"
              >
                {inTab(current).length === 0 && (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {uiText('noRolesMatch')}
                  </p>
                )}
                {inTab(current).map(role => (
                  <label
                    key={role.id}
                    className="flex cursor-pointer items-start gap-3 rounded-2xl px-3 py-2 hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                      checked={selected.includes(role.id)}
                      disabled={disabled}
                      aria-labelledby={`${id}-${role.id}`}
                      aria-describedby={
                        role.description ? `${id}-${role.id}-description` : undefined
                      }
                      onChange={event =>
                        update(
                          event.target.checked
                            ? [...selected, role.id]
                            : selected.filter(id => id !== role.id)
                        )
                      }
                    />
                    <span className="min-w-0">
                      <span id={`${id}-${role.id}`} className="block text-sm">
                        {role.displayName}
                        {role.isActive === false ? ` · ${uiText('inactiveRole')}` : ''}
                      </span>
                      {role.description && (
                        <span
                          id={`${id}-${role.id}-description`}
                          className="line-clamp-2 block text-xs text-muted-foreground"
                        >
                          {role.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground">
            {uiText('rolesSelected', { count: selected.length })}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || selected.length === 0}
              onClick={() => update([])}
            >
              {uiText('clear')}
            </Button>
            <Button type="button" size="sm" onClick={() => changeOpen(false)}>
              {uiText('done')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
